import { useState, useEffect } from 'react'
import MobileLayout from '../../components/MobileLayout'
import { employeeApi } from '../../lib/employeeApi'

interface TimesheetLine {
  id: string
  date: string
  plannedStart?: string
  plannedEnd?: string
  validatedStart?: string
  validatedEnd?: string
  breakMinutes: number
  netHours: number
  isAbsence: boolean
  correctionNote?: string
  status: string
}

interface Timesheet {
  id: string
  weekStart: string
  weekEnd: string
  status: string
  totalHours: number
  overtimeHours: number
  note?: string
  lines: TimesheetLine[]
}

const STATUS_LABELS: Record<string, string> = {
  draft:     'En cours de vérification',
  validated: 'Validée ✓',
  signed:    'Signée ✓'
}
const STATUS_COLORS: Record<string, string> = {
  draft:     'bg-gray-100 text-gray-500',
  validated: 'bg-green-100 text-green-700',
  signed:    'bg-green-100 text-green-700'
}

const DAYS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
const MONTHS = ['jan', 'fév', 'mar', 'avr', 'mai', 'juin', 'juil', 'août', 'sep', 'oct', 'nov', 'déc']

function fmtWeek(start: string, end: string) {
  const s = new Date(start)
  const e = new Date(end)
  return `${s.getDate()} ${MONTHS[s.getMonth()]} – ${e.getDate()} ${MONTHS[e.getMonth()]} ${e.getFullYear()}`
}

function fmtDate(d: string) {
  const dt = new Date(d)
  return `${DAYS[dt.getDay()]} ${dt.getDate()}/${dt.getMonth() + 1}`
}

interface DisputeCorrection {
  date: string
  originalStart: string
  originalEnd: string
  newStart: string
  newEnd: string
}

export default function MobileTimesheets() {
  const [timesheets, setTimesheets] = useState<Timesheet[]>([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [signing, setSigning] = useState<string | null>(null)
  const [signNote, setSignNote] = useState('')
  const [disputing, setDisputing] = useState<string | null>(null)
  const [disputeComment, setDisputeComment] = useState('')
  const [showDispute, setShowDispute] = useState<string | null>(null)
  const [disputeCorrections, setDisputeCorrections] = useState<DisputeCorrection[]>([])

  useEffect(() => {
    load()
  }, [])

  async function load() {
    setLoading(true)
    const r = await employeeApi.get('/employee/me/timesheets')
    setTimesheets(r.data)
    setLoading(false)
  }

  async function sign(id: string) {
    setSigning(id)
    try {
      await employeeApi.post(`/employee/me/timesheets/${id}/sign`, { note: signNote || undefined })
      setSignNote('')
      await load()
      setExpanded(null)
    } finally {
      setSigning(null)
    }
  }

  function openDispute(ts: Timesheet) {
    // Pré-remplir avec toutes les lignes travaillées
    const corrections = ts.lines
      .filter(l => !l.isAbsence && l.validatedStart && l.validatedEnd)
      .map(l => ({
        date: l.date,
        originalStart: l.validatedStart!,
        originalEnd: l.validatedEnd!,
        newStart: l.validatedStart!,
        newEnd: l.validatedEnd!,
      }))
    setDisputeCorrections(corrections)
    setDisputeComment('')
    setShowDispute(ts.id)
  }

  function adjustTime(date: string, field: 'newStart' | 'newEnd', delta: number) {
    setDisputeCorrections(prev => prev.map(c => {
      if (c.date !== date) return c
      const [h, m] = c[field].split(':').map(Number)
      const total = Math.max(0, Math.min(23 * 60 + 59, h * 60 + m + delta))
      const nh = String(Math.floor(total / 60)).padStart(2, '0')
      const nm = String(total % 60).padStart(2, '0')
      return { ...c, [field]: `${nh}:${nm}` }
    }))
  }

  function setTime(date: string, field: 'newStart' | 'newEnd', value: string) {
    setDisputeCorrections(prev => prev.map(c => c.date === date ? { ...c, [field]: value } : c))
  }

  async function dispute(id: string) {
    const changed = disputeCorrections.filter(c => c.newStart !== c.originalStart || c.newEnd !== c.originalEnd)
    if (changed.length === 0 && !disputeComment.trim()) return
    setDisputing(id)

    const DAYS_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
    const parts: string[] = changed.map(c => {
      const d = new Date(c.date)
      const dayName = DAYS_FR[d.getDay()]
      const dateStr = `${d.getDate()}/${d.getMonth() + 1}`
      const lines: string[] = []
      if (c.newStart !== c.originalStart) lines.push(`début ${c.originalStart}→${c.newStart}`)
      if (c.newEnd !== c.originalEnd) lines.push(`fin ${c.originalEnd}→${c.newEnd}`)
      return `${dayName} ${dateStr} : ${lines.join(', ')}`
    })
    if (disputeComment.trim()) parts.push(disputeComment.trim())
    const reason = parts.join(' | ')

    // Corrections structurées pour auto-application côté manager
    const structuredCorrections = changed.map(c => ({
      date: c.date,
      newStart: c.newStart,
      newEnd: c.newEnd
    }))

    try {
      await employeeApi.post(`/employee/me/timesheets/${id}/dispute`, {
        reason,
        corrections: structuredCorrections
      })
      setDisputeComment('')
      setDisputeCorrections([])
      setShowDispute(null)
      await load()
    } finally {
      setDisputing(null)
    }
  }

  return (
    <MobileLayout>
      <div className="p-4 space-y-4">
        <h1 className="text-lg font-bold text-gray-800">Feuilles d'heures</h1>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Chargement...</div>
        ) : timesheets.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm text-gray-400">
            Aucune feuille d'heures
          </div>
        ) : (
          timesheets.map(ts => (
            <div key={ts.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {/* Header */}
              <button
                onClick={() => setExpanded(e => e === ts.id ? null : ts.id)}
                className="w-full px-4 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="text-left">
                  <div className="font-semibold text-gray-800 text-sm">{fmtWeek(ts.weekStart, ts.weekEnd)}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {ts.totalHours.toFixed(1)}h
                    {ts.overtimeHours > 0 && <span className="text-orange-500 ml-2">+{ts.overtimeHours.toFixed(1)}h sup.</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLORS[ts.status] || 'bg-gray-100 text-gray-600'}`}>
                    {STATUS_LABELS[ts.status] || ts.status}
                  </span>
                  <span className="text-gray-400 text-sm">{expanded === ts.id ? '▲' : '▼'}</span>
                </div>
              </button>

              {/* Details */}
              {expanded === ts.id && (
                <div className="border-t border-gray-100">
                  {/* Lines */}
                  {ts.lines.map((line, i) => {
                    const isCorrected = line.status === 'corrected'
                    const hoursChanged = isCorrected && line.plannedStart && (line.validatedStart !== line.plannedStart || line.validatedEnd !== line.plannedEnd)
                    return (
                      <div key={line.id} className={`px-4 py-2.5 ${i > 0 ? 'border-t border-gray-50' : ''} text-sm`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="font-medium text-gray-700">{fmtDate(line.date)}</span>
                            {line.isAbsence && <span className="ml-2 text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded">Absence</span>}
                            {isCorrected && <span className="ml-2 text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded">✏️ Corrigé</span>}
                          </div>
                          <div className="text-right">
                            {line.validatedStart ? (
                              <span className={`font-medium ${isCorrected ? 'text-orange-600' : 'text-gray-700'}`}>
                                {line.validatedStart} – {line.validatedEnd}
                              </span>
                            ) : line.plannedStart ? (
                              <span className="text-gray-400">{line.plannedStart} – {line.plannedEnd}</span>
                            ) : (
                              <span className="text-gray-300 italic">—</span>
                            )}
                            <div className="text-xs text-blue-600 font-medium">{line.netHours.toFixed(1)}h net</div>
                          </div>
                        </div>
                        {/* Horaires prévus si corrigés */}
                        {hoursChanged && (
                          <div className="mt-1 text-xs text-gray-400 flex items-center gap-1">
                            <span>Prévu :</span>
                            <span className="line-through">{line.plannedStart} – {line.plannedEnd}</span>
                          </div>
                        )}
                        {/* Note de correction */}
                        {line.correctionNote && !line.isAbsence && (
                          <p className="mt-1 text-xs text-orange-500 italic">{line.correctionNote}</p>
                        )}
                      </div>
                    )
                  })}

                  {/* Total */}
                  <div className="px-4 py-3 bg-gray-50 flex justify-between font-semibold text-sm">
                    <span className="text-gray-700">Total semaine</span>
                    <span className="text-blue-600">{ts.totalHours.toFixed(1)}h</span>
                  </div>

                  {/* Contester si erreur */}
                  {ts.status === 'validated' && (
                    <div className="px-4 pb-4 pt-2">
                      {showDispute !== ts.id ? (
                        <button
                          onClick={() => openDispute(ts)}
                          className="w-full py-2 border border-gray-200 text-gray-400 rounded-xl text-xs transition-colors hover:border-red-300 hover:text-red-500"
                        >
                          ⚠️ Signaler une erreur sur ces heures
                        </button>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm font-bold text-red-700">Indiquez les corrections souhaitées</p>

                          {/* Ligne par jour travaillé */}
                          {disputeCorrections.map(c => {
                            const d = new Date(c.date)
                            const DAYS_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
                            const dayLabel = `${DAYS_FR[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`
                            const startChanged = c.newStart !== c.originalStart
                            const endChanged = c.newEnd !== c.originalEnd
                            const hasChange = startChanged || endChanged
                            return (
                              <div key={c.date} className={`rounded-xl border p-3 space-y-2 ${hasChange ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-white'}`}>
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-semibold text-gray-800">{dayLabel}</span>
                                  {hasChange && <span className="text-xs text-orange-600 font-medium">Modifié</span>}
                                </div>
                                {/* Début */}
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-500 w-14">Début</span>
                                  <button onClick={() => adjustTime(c.date, 'newStart', -30)} className="w-8 h-8 rounded-lg bg-gray-100 text-gray-700 font-bold text-lg flex items-center justify-center hover:bg-gray-200">−</button>
                                  <input type="time" value={c.newStart}
                                    onChange={e => setTime(c.date, 'newStart', e.target.value)}
                                    className={`flex-1 border rounded-lg px-2 py-1.5 text-sm text-center font-semibold focus:outline-none focus:ring-2 focus:ring-orange-300 ${startChanged ? 'border-orange-400 text-orange-700' : 'border-gray-200 text-gray-700'}`} />
                                  <button onClick={() => adjustTime(c.date, 'newStart', 30)} className="w-8 h-8 rounded-lg bg-gray-100 text-gray-700 font-bold text-lg flex items-center justify-center hover:bg-gray-200">+</button>
                                  <span className="text-xs text-gray-400 w-14 text-right">prévu {c.originalStart}</span>
                                </div>
                                {/* Fin */}
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-500 w-14">Fin</span>
                                  <button onClick={() => adjustTime(c.date, 'newEnd', -30)} className="w-8 h-8 rounded-lg bg-gray-100 text-gray-700 font-bold text-lg flex items-center justify-center hover:bg-gray-200">−</button>
                                  <input type="time" value={c.newEnd}
                                    onChange={e => setTime(c.date, 'newEnd', e.target.value)}
                                    className={`flex-1 border rounded-lg px-2 py-1.5 text-sm text-center font-semibold focus:outline-none focus:ring-2 focus:ring-orange-300 ${endChanged ? 'border-orange-400 text-orange-700' : 'border-gray-200 text-gray-700'}`} />
                                  <button onClick={() => adjustTime(c.date, 'newEnd', 30)} className="w-8 h-8 rounded-lg bg-gray-100 text-gray-700 font-bold text-lg flex items-center justify-center hover:bg-gray-200">+</button>
                                  <span className="text-xs text-gray-400 w-14 text-right">prévu {c.originalEnd}</span>
                                </div>
                              </div>
                            )
                          })}

                          {/* Commentaire libre optionnel */}
                          <textarea
                            value={disputeComment}
                            onChange={e => setDisputeComment(e.target.value)}
                            placeholder="Commentaire (optionnel)..."
                            rows={2}
                            className="w-full border border-gray-200 rounded-xl p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-300"
                          />

                          <div className="flex gap-2">
                            <button
                              onClick={() => { setShowDispute(null); setDisputeCorrections([]) }}
                              className="flex-1 py-2.5 border border-gray-300 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50"
                            >
                              Annuler
                            </button>
                            <button
                              onClick={() => dispute(ts.id)}
                              disabled={
                                (disputeCorrections.every(c => c.newStart === c.originalStart && c.newEnd === c.originalEnd) && !disputeComment.trim())
                                || disputing === ts.id
                              }
                              className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-semibold disabled:opacity-50"
                            >
                              {disputing === ts.id ? 'Envoi...' : 'Envoyer le refus'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {ts.status === 'draft' && ts.note?.startsWith('⚠️') && (
                    <div className="mx-4 mb-4 bg-orange-50 border border-orange-200 rounded-xl p-3">
                      <p className="text-xs font-semibold text-orange-700 mb-0.5">Contestation envoyée</p>
                      <p className="text-xs text-orange-600">{ts.note.replace('⚠️ Contestée par le salarié : ', '')}</p>
                      <p className="text-xs text-gray-400 mt-1">Votre responsable a été notifié et va corriger la feuille.</p>
                    </div>
                  )}

                  {ts.status === 'signed' && ts.note && !ts.note.startsWith('⚠️') && (
                    <div className="px-4 pb-4">
                      <p className="text-xs text-gray-400 italic">"{ts.note}"</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </MobileLayout>
  )
}
